import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchJson } from "./api";
import { useTranslation } from "react-i18next";

const GOLD = "#d4a550";
const GOLD_DIM = "rgba(212,165,80,0.12)";
const GOLD_BORDER = "rgba(212,165,80,0.3)";
const BG = "#07070a";
const SURFACE = "#0f0f17";
const BORDER = "rgba(255,255,255,0.07)";
const TEXT = "#e8e6df";
const TEXT_DIM = "rgba(232,230,223,0.45)";

function formatDate(d, lang="it") {
  if (!d) return "";
  return new Date(d).toLocaleDateString(lang==="en"?"en-GB":"it-IT", { weekday:"long", day:"numeric", month:"long", year:"numeric" });
}
function formatTime(d, lang="it") {
  if (!d) return "";
  return new Date(d).toLocaleTimeString(lang==="en"?"en-GB":"it-IT", { hour:"2-digit", minute:"2-digit" });
}
function formatMsgTime(d, lang="it") {
  if (!d) return "";
  return new Date(d).toLocaleTimeString(lang==="en"?"en-GB":"it-IT", { hour:"2-digit", minute:"2-digit" });
}
function isPast(d) { return new Date(d) < new Date(); }

export default function EventDetailPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "it";
  const { id } = useParams();
  const nav = useNavigate();
  const [event, setEvent] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [joining, setJoining] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const lastMsgRef = useRef(null);
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();

  async function load() {
    try {
      const d = await fetchJson(`/events/${id}`);
      setEvent(d.event);
      setParticipants(d.participants || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function loadMessages() {
    try {
      const d = await fetchJson(`/events/${id}/messages`);
      const msgs = d.messages || [];
      setMessages(msgs);
      if (msgs.length > 0) {
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior:"smooth", block:"nearest" }), 100);
      }
    } catch(e) { console.error(e); }
  }

  useEffect(() => { load(); loadMessages(); }, [id]);

  // Polling messaggi ogni 5 secondi
  useEffect(() => {
    const t = setInterval(loadMessages, 5000);
    return () => clearInterval(t);
  }, [id]);

  async function handleJoin() {
    setJoining(true);
    try {
      if (Number(event.is_joined) === 1) {
        await fetchJson(`/events/${id}/join`, { method:"DELETE" });
      } else {
        await fetchJson(`/events/${id}/join`, { method:"POST" });
      }
      await load();
    } catch(e) { console.error(e); }
    finally { setJoining(false); }
  }

  async function handleSendMsg() {
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      await fetchJson(`/events/${id}/messages`, {
        method:"POST",
        body: JSON.stringify({ content: newMsg.trim() })
      });
      setNewMsg("");
      await loadMessages();
    } catch(e) { console.error(e); }
    finally { setSending(false); }
  }

  if (loading) return <div style={{ background:BG, minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", color:TEXT_DIM }}>{t("events.detail_loading")}</div>;
  if (!event) return (
    <div style={{ background:BG, minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", color:TEXT_DIM, gap:12 }}>
      <div style={{ fontSize:16 }}>{t("events.detail_not_found")}</div>
      <div style={{ fontSize:13 }}>{t("events.detail_no_access")}</div>
      <button onClick={() => nav("/eventi")} style={{ marginTop:8, background:"none", border:`1px solid ${BORDER}`, borderRadius:6, color:TEXT_DIM, cursor:"pointer", padding:"8px 16px", fontSize:13 }}>{t("events.detail_back_btn")}</button>
    </div>
  );

  const past = isPast(event.event_date);
  const isJoined = Number(event.is_joined) === 1;
  const isFull = event.max_participants && Number(event.participant_count) >= Number(event.max_participants) && !isJoined;
  const isAuthor = Number(event.creator_id) === Number(currentUser.id);
  const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent((event.address || "") + " " + (event.city || ""))}`;

  return (
    <div style={{ minHeight:"100vh", background:BG, color:TEXT, fontFamily:"'Kanit','Inter',sans-serif", padding:"32px 24px" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24 }}>
        <button onClick={() => nav("/eventi")} style={{ background:"none", border:"none", color:TEXT_DIM, cursor:"pointer", fontSize:13, padding:0 }}>
          {t("events.detail_back")}
        </button>
        {isAuthor && (
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={() => nav(`/eventi/${id}/edit`)}
              style={{ background:"none", border:`1px solid ${BORDER}`, borderRadius:6, color:TEXT_DIM, cursor:"pointer", fontSize:12, padding:"5px 12px" }}>
              {t("events.detail_edit")}
            </button>
            <button onClick={async () => {
              if (!window.confirm(t("events.detail_confirm_delete"))) return;
              await fetchJson(`/events/${id}`, { method:"DELETE" });
              nav("/eventi");
            }}
              style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.25)", borderRadius:6, color:"#f87171", cursor:"pointer", fontSize:12, padding:"5px 12px" }}>
              {t("events.detail_delete")}
            </button>
          </div>
        )}
      </div>

      {/* Header evento */}
      <div style={{ background:SURFACE, border:`1px solid ${BORDER}`, borderRadius:12, padding:"28px 32px", marginBottom:20 }}>
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12, marginBottom:16 }}>
          <div>
            {past ? (
              <span style={{ fontSize:10, fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase", color:"rgba(232,230,223,0.3)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:20, padding:"2px 8px", marginBottom:8, display:"inline-block" }}>Scaduto</span>
            ) : (
              <span style={{ fontSize:10, fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase", color:"#4ade80", border:"1px solid rgba(74,222,128,0.3)", borderRadius:20, padding:"2px 8px", marginBottom:8, display:"inline-block" }}>Aperto</span>
            )}
            <h1 style={{ margin:"8px 0 0 0", fontSize:22, fontWeight:800, color:TEXT }}>{event.title}</h1>
          </div>
          {!past && (
            <button onClick={handleJoin} disabled={joining||isFull}
              style={{ background:isJoined?"rgba(239,68,68,0.1)":GOLD_DIM, border:`1px solid ${isJoined?"rgba(239,68,68,0.3)":GOLD_BORDER}`, borderRadius:8, color:isJoined?"#f87171":GOLD, cursor:"pointer", padding:"10px 20px", fontSize:13, fontWeight:700, flexShrink:0 }}>
              {joining ? t("events.detail_joining") : isJoined ? t("events.detail_leave") : isFull ? t("events.detail_full") : t("events.detail_join")}
            </button>
          )}
        </div>

        <div style={{ display:"flex", flexDirection:"column", gap:8, fontSize:13, color:TEXT_DIM }}>
          <div>📅 {formatDate(event.event_date, lang)} {t("events.detail_at")} {formatTime(event.event_date, lang)}</div>
          {(event.city || event.address) && (
            <div>
              📍 {event.address}{event.city ? `, ${event.city}` : ""}
              {" "}<a href={mapsUrl} target="_blank" rel="noreferrer" style={{ color:GOLD, fontSize:11 }}>→ Google Maps</a>
            </div>
          )}
          {event.visibility === 'roles' && event.visibility_filter?.roles?.length > 0 && (
            <div style={{ color:"rgba(212,165,80,0.7)", fontSize:11 }}>Riservato a: {(event.visibility_filter?.roles||[]).join(", ")}</div>
          )}
          <div>
            👥 {event.participant_count} partecipanti
            {" · "}
            {event.is_anonymous ? t("events.detail_organizer_anon") : `${t("events.detail_organized_by")} ${event.creator_nome} ${event.creator_cognome}`}
          </div>
        </div>

        {event.description && (
          <div style={{ marginTop:16, fontSize:13, color:TEXT, lineHeight:1.7, borderTop:`1px solid ${BORDER}`, paddingTop:16 }}>
            {event.description}
          </div>
        )}
      </div>

      {/* Partecipanti */}
      {participants.length > 0 && (
        <div style={{ background:SURFACE, border:`1px solid ${BORDER}`, borderRadius:12, padding:"20px 24px", marginBottom:20 }}>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:TEXT_DIM, marginBottom:12 }}>
            {t("events.detail_who")} ({participants.length})
          </div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {participants.map(p => (
              <div key={p.id} style={{ display:"flex", alignItems:"center", gap:6, background:"rgba(255,255,255,0.03)", border:`1px solid ${BORDER}`, borderRadius:20, padding:"4px 10px" }}>
                <div style={{ width:20, height:20, borderRadius:"50%", background:GOLD_DIM, border:`1px solid ${GOLD_BORDER}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:700, color:GOLD }}>
                  {(p.nome||"")[0]}{(p.cognome||"")[0]}
                </div>
                <span style={{ fontSize:12, color:TEXT }}>{p.nome} {p.cognome}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Chat */}
      <div style={{ background:SURFACE, border:`1px solid ${BORDER}`, borderRadius:12, overflow:"hidden" }}>
        <div style={{ padding:"16px 20px", borderBottom:`1px solid ${BORDER}`, fontSize:11, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:TEXT_DIM }}>
          {t("events.chat_title")}
        </div>
        <div style={{ height:300, overflowY:"auto", padding:"16px 20px", display:"flex", flexDirection:"column", gap:12, scrollbarWidth:"none", msOverflowStyle:"none" }}>
          {messages.length === 0 && (
            <div style={{ textAlign:"center", color:TEXT_DIM, fontSize:12, marginTop:40 }}>{t("events.chat_empty")}</div>
          )}
          {messages.map(m => {
            const isMe = m.user_id === currentUser.id;
            return (
              <div key={m.id} style={{ display:"flex", flexDirection:"column", alignItems:isMe?"flex-end":"flex-start" }}>
                <div style={{ fontSize:10, color:TEXT_DIM, marginBottom:3 }}>
                  {!isMe && `${m.nome} ${m.cognome} · `}{formatMsgTime(m.created_at, lang)}
                </div>
                <div style={{ background:isMe?GOLD_DIM:"rgba(255,255,255,0.04)", border:`1px solid ${isMe?GOLD_BORDER:BORDER}`, borderRadius:10, padding:"8px 14px", maxWidth:"75%", fontSize:13, color:TEXT, lineHeight:1.5 }}>
                  {m.content}
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
        <div style={{ padding:"12px 16px", borderTop:`1px solid ${BORDER}`, display:"flex", gap:10 }}>
          <input value={newMsg} onChange={e => setNewMsg(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleSendMsg()}
placeholder={t("events.chat_placeholder")}
            style={{ flex:1, background:"rgba(255,255,255,0.04)", border:`1px solid ${BORDER}`, borderRadius:8, color:TEXT, fontSize:13, padding:"8px 12px", outline:"none", fontFamily:"inherit" }} />
          <button onClick={handleSendMsg} disabled={sending||!newMsg.trim()}
            style={{ background:GOLD_DIM, border:`1px solid ${GOLD_BORDER}`, borderRadius:8, color:GOLD, cursor:"pointer", padding:"8px 16px", fontSize:13, fontWeight:700 }}>
            {t("events.chat_send")}
          </button>
        </div>
      </div>
    </div>
  );
}
